import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  Tag,
  Info,
  ShieldCheck,
  ChevronRight,
  Home,
  CheckCircle2,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import {
  SITE_BASE_URL,
  getCategoryBySlug,
} from '../data/productCategories';
import {
  getItemBySlugs,
  itemPath,
  getItemsForBrand,
} from '../data/productItems';
import {
  buildProductPlaceholder,
  buildProductImageAlt,
} from '../lib/productPlaceholder';

const Breadcrumb = ({ category, item }) => (
  <nav aria-label="Breadcrumb" className="mb-6">
    <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-400">
      <li>
        <Link to="/" className="hover:text-[#ff6b35] transition-colors flex items-center gap-1">
          <Home size={14} aria-hidden="true" />
          Home
        </Link>
      </li>
      <li aria-hidden="true">
        <ChevronRight size={14} />
      </li>
      <li>
        <Link to="/#products" className="hover:text-[#ff6b35] transition-colors">
          Product categories
        </Link>
      </li>
      <li aria-hidden="true">
        <ChevronRight size={14} />
      </li>
      <li>
        <Link
          to={`/category/${category.slug}`}
          className="hover:text-[#ff6b35] transition-colors"
        >
          {category.title}
        </Link>
      </li>
      <li aria-hidden="true">
        <ChevronRight size={14} />
      </li>
      <li className="text-[#ff6b35] font-medium" aria-current="page">
        {item.name} ({item.partNumber})
      </li>
    </ol>
  </nav>
);

const ItemDetail = () => {
  const { brandSlug, itemSlug } = useParams();
  const category = getCategoryBySlug(brandSlug);
  const item = getItemBySlugs(brandSlug, itemSlug);
  const { toast } = useToast();

  if (!category || !item) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex flex-col">
        <Navigation />
        <main className="flex-grow pt-28 pb-20 px-4 text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Part not found</h1>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">
            This SKU is not currently listed in our catalog. Contact us if you need help sourcing it.
          </p>
          <Button asChild className="bg-[#ff6b35] hover:bg-[#ff8c42] text-white">
            <Link to="/">Return home</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const brandLabel = category.brandLabel || category.title;
  const currentUrl = `${SITE_BASE_URL}${itemPath(category.slug, item.slug)}`;
  const placeholderSrc = buildProductPlaceholder({
    partNumber: item.partNumber,
    productName: item.name,
    brandLabel,
  });
  const imgAlt = buildProductImageAlt({
    productName: item.name,
    partNumber: item.partNumber,
    brandLabel,
  });

  const handleRequestQuote = () => {
    toast({
      title: 'Quote Request Initiated',
      description: `Inquiry for "${item.name}" (Code: ${item.partNumber}). Our team will contact you shortly.`,
    });
  };

  const relatedItems = getItemsForBrand(category.slug)
    .filter((other) => other.slug !== item.slug)
    .slice(0, 3);

  const seoTitle = `${item.name} ${item.partNumber} | ${brandLabel} Spare Parts | Trade Zone Equipments`;
  const metaDescription = item.description.length > 160
    ? `${item.description.slice(0, 157).trim()}...`
    : item.description;
  const keywordsCsv = [
    item.keywords,
    `${brandLabel} spare parts`,
    `${item.name} ${item.partNumber}`,
    'heavy equipment parts',
    'wheel loader spare parts',
  ]
    .filter(Boolean)
    .join(', ');

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${item.name} - ${item.partNumber}`,
    image: placeholderSrc,
    description: item.description,
    sku: item.partNumber,
    mpn: item.partNumber,
    brand: {
      '@type': 'Brand',
      name: brandLabel,
    },
    category: category.title,
    manufacturer: {
      '@type': 'Organization',
      name: 'Trade Zone Equipments',
      url: SITE_BASE_URL,
    },
    offers: {
      '@type': 'Offer',
      url: currentUrl,
      priceCurrency: 'USD',
      price: '0.00',
      priceValidUntil: '2026-12-31',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: 'Trade Zone Equipments',
        url: SITE_BASE_URL,
      },
    },
    isRelatedTo: relatedItems.map((related) => ({
      '@type': 'Product',
      name: `${related.name} ${related.partNumber}`,
      url: `${SITE_BASE_URL}${itemPath(category.slug, related.slug)}`,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_BASE_URL },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Product categories',
        item: `${SITE_BASE_URL}/#products`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: category.title,
        item: `${SITE_BASE_URL}/category/${category.slug}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: `${item.name} (${item.partNumber})`,
        item: currentUrl,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywordsCsv} />
        <link rel="canonical" href={currentUrl} />

        <meta property="og:type" content="product" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={placeholderSrc} />
        <meta property="og:image:alt" content={imgAlt} />
        <meta property="product:brand" content={brandLabel} />
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={placeholderSrc} />

        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#1a1a1a] flex flex-col font-sans">
        <Navigation />

        <main className="flex-grow pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <article
            className="max-w-7xl mx-auto"
            itemScope
            itemType="https://schema.org/Product"
          >
            <Breadcrumb category={category} item={item} />

            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="mb-12 border-b border-[#2d2d2d] pb-8"
            >
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                  <span className="text-[#ff6b35] font-semibold text-sm tracking-wider uppercase mb-2 block">
                    {category.title}
                  </span>
                  <h1
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2"
                    itemProp="name"
                  >
                    {item.name} - <span className="text-[#ff6b35]">{item.partNumber}</span>
                  </h1>
                  {item.cnName && (
                    <p className="text-gray-400 text-sm mt-1">
                      <span className="opacity-60 mr-2">CN:</span>
                      <span lang="zh">{item.cnName}</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-[#2d2d2d] px-4 py-2 rounded-lg border border-[#3d3d3d]">
                    <span className="text-gray-400 text-xs uppercase block">Part Number</span>
                    <span className="text-white font-mono font-bold" itemProp="sku">
                      {item.partNumber}
                    </span>
                  </div>
                </div>
              </div>
            </motion.header>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <section aria-label="Product Image">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="relative rounded-2xl overflow-hidden bg-[#2d2d2d] shadow-2xl border border-[#3d3d3d] group"
                >
                  <div className="aspect-[4/3] bg-white/5">
                    <img
                      src={placeholderSrc}
                      alt={imgAlt}
                      title={`${item.name} ${item.partNumber}`}
                      loading="eager"
                      width="800"
                      height="600"
                      itemProp="image"
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-[#ff6b35] text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg flex items-center gap-1">
                      <Tag size={14} aria-hidden="true" />
                      In Stock
                    </span>
                  </div>
                </motion.div>
              </section>

              <section className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Product overview</h2>
                  <p
                    className="text-lg text-gray-300 leading-relaxed mb-6"
                    itemProp="description"
                  >
                    {item.description}
                  </p>
                </div>

                {item.applicationModels?.length > 0 && (
                  <div className="bg-[#2d2d2d]/50 rounded-xl p-6 border border-[#3d3d3d]">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Info size={18} className="text-[#ff6b35]" aria-hidden="true" />
                      Compatible {brandLabel} models
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {item.applicationModels.map((model) => (
                        <li
                          key={model}
                          className="flex items-start gap-2 text-gray-300 text-sm"
                        >
                          <CheckCircle2
                            size={16}
                            className="text-[#ff6b35] shrink-0 mt-0.5"
                            aria-hidden="true"
                          />
                          <span>{model}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="pt-6 border-t border-[#2d2d2d]">
                  <Button
                    onClick={handleRequestQuote}
                    className="w-full bg-[#ff6b35] hover:bg-[#ff8c42] text-white py-6 text-lg rounded-lg transition-all duration-300 shadow-lg shadow-[#ff6b35]/20 hover:shadow-[#ff6b35]/40"
                    aria-label={`Request quote for ${item.name} ${item.partNumber}`}
                  >
                    Request quote
                  </Button>
                  <div className="flex items-center gap-2 text-gray-400 text-sm mt-4 justify-center sm:justify-start">
                    <ShieldCheck size={18} className="text-[#ff6b35]" aria-hidden="true" />
                    <span>OEM-grade quality and fitment</span>
                  </div>
                  <meta itemProp="brand" content="Trade Zone Equipments" />
                  <meta itemProp="mpn" content={item.partNumber} />
                </div>
              </section>
            </div>

            {relatedItems.length > 0 && (
              <section className="mt-20" aria-labelledby="related-heading">
                <h2 id="related-heading" className="text-2xl font-bold text-white mb-6">
                  More {brandLabel} parts
                </h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedItems.map((related) => {
                    const relatedSrc = buildProductPlaceholder({
                      partNumber: related.partNumber,
                      productName: related.name,
                      brandLabel,
                    });
                    const relatedAlt = buildProductImageAlt({
                      productName: related.name,
                      partNumber: related.partNumber,
                      brandLabel,
                    });
                    return (
                      <li key={related.slug}>
                        <Link
                          to={itemPath(category.slug, related.slug)}
                          className="block bg-[#2d2d2d] rounded-xl border border-[#3d3d3d] overflow-hidden hover:border-[#ff6b35]/60 transition-all duration-300 group h-full flex flex-col"
                        >
                          <div className="aspect-[4/3] bg-[#1f1f1f] overflow-hidden">
                            <img
                              src={relatedSrc}
                              alt={relatedAlt}
                              loading="lazy"
                              width="600"
                              height="450"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-5">
                            <h3 className="text-base font-semibold text-white group-hover:text-[#ff6b35] transition-colors">
                              {related.name}
                            </h3>
                            <p className="text-xs uppercase tracking-wider text-gray-400 mt-1 font-mono">
                              {related.partNumber}
                            </p>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ItemDetail;
